import { PublicLayoutContent } from '@components/layout/public';
import { ActivationOverviewContext } from '@extendedWarranty/contexts/ActivationOverviewContext';
import { useExtendedWarrantyContext } from '@extendedWarranty/contexts/ExtendedWarrantyContext';
import { UploadedInstallationOverview } from '@extendedWarranty/routes/dashboard/ActivationOverview';
import { InstallationData, InstallationDto, ISerialSubmission } from '@extendedWarranty/types/extendedWarranty';
import { areDuplicatesFixed } from '@extendedWarranty/utils/extendedWarranty';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, CircularProgress, Typography } from '@mui/material';
import { handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

const ActivationOverview = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { installations, setInstallations, validateInstallations, setViewIndex } = useExtendedWarrantyContext();
    const [isPEEnabled, setIsPEEnabled] = useState(false);

    const hasUneditErrors = useMemo(
        () =>
            (installations &&
                Object.keys(installations).some(
                    (installation) =>
                        installations[installation].serial_numbers.filter(
                            (sn) => !!sn.ValidationMessageError || !areDuplicatesFixed(installations, sn)
                        ).length > installations[installation].serial_numbers.filter((sn) => !!sn.edit_value).length
                )) ||
            false,
        [installations]
    );

    const areInstallationsEmpty = useMemo(
        () =>
            installations &&
            Object.keys(installations).every((installation) => installations[installation].serial_numbers.length <= 0),
        [installations]
    );

    const areAllInstallationsVerified = useMemo(() => {
        return (
            installations &&
            Object.keys(installations).every((installation) => installations[installation].action !== 'verify')
        );
    }, [installations]);

    const thereAreNoErrorsOrEdits = (newOverview: Record<string, InstallationData>) =>
        newOverview &&
        Object.keys(newOverview)
            .flatMap((installation) => newOverview[installation].serial_numbers)
            .filter((sn) => !!sn.ValidationMessageError || sn.edit_value).length === 0;

    const handleSerialNumberRemove = useCallback(
        (installation_name: string, id: string) => {
            const newOverview = { ...installations };
            newOverview[installation_name].serial_numbers = newOverview[installation_name].serial_numbers.filter(
                (sn) => sn.id !== id
            );

            if (thereAreNoErrorsOrEdits(newOverview)) {
                newOverview[installation_name].action = 'save';
            }

            setInstallations(newOverview);
        },
        [installations, setInstallations]
    );

    const handleSerialNumberUndo = (installationId: string, id: string) => {
        const newOverview = { ...installations };
        newOverview[installationId].serial_numbers = newOverview[installationId].serial_numbers.map((sn) => {
            if (sn.id === id) sn.edit_value = '';

            return sn;
        });
        setInstallations(newOverview);
    };

    const handleEditConfirm = (installationId: string, newInstallationData: Record<string, string>) => {
        if (installations) {
            const installationSuccesses = installations[installationId].serial_numbers.filter(
                (sn) => !sn.ValidationMessageError && !sn.isDuplicate
            );
            const installationErrors = installations[installationId].serial_numbers.filter(
                (sn) => !!sn.ValidationMessageError || sn.isDuplicate
            );

            const newInstallationSNs = [...installationErrors]
                .map((snError) => {
                    if (!newInstallationData[snError.id as string]) return null;
                    else if (newInstallationData[snError.id as string] !== snError.SerialNumber)
                        return { ...snError, edit_value: newInstallationData[snError.id as string] };
                    return snError;
                })
                .filter((sn) => sn) as ISerialSubmission[];

            const newInstallations = {
                ...installations,
                [installationId]: {
                    ...installations[installationId],
                    serial_numbers: [...installationSuccesses, ...newInstallationSNs],
                },
            };

            setInstallations(newInstallations);
        }
    };

    const handleInstallationSubmit = async () => {
        try {
            setIsLoading(true);
            const newInstallations = Object.keys(installations).reduce(
                (acc, curr) => ({
                    ...acc,
                    [curr]: {
                        serial_numbers: installations[curr].serial_numbers.map((sn) => ({
                            serial_number: sn.edit_value || (sn.SerialNumber as string),
                            date_installed: format(installations[curr].date_installed, 'yyyy-MM-dd'),
                        })),
                        date: format(installations[curr].date_installed, 'yyyy-MM-dd'),
                        support: installations[curr].support,
                        id: installations[curr].id,
                    },
                }),
                {} as InstallationDto
            );
            const { result, status } = await validateInstallations(newInstallations);
            if (!Object.keys(result).find((name) => result[name].action !== 'complete')) {
                setViewIndex(3);
            }

            if (status === 'NOK') {
                handleError('Some serial numbers are incorrect', { msg: '' }, { className: 'mb-20 md:mb-0' });
            } else if (
                status === 'OK' &&
                Object.keys(result).every((installation) => result[installation].action !== 'verify') &&
                !areAllInstallationsVerified
            ) {
                toaster.success({ msg: 'Serial numbers verified. Confirm to save.' }, { className: 'mb-20 md:mb-0' });
            }
            setInstallations(result);
        } catch (err) {
            handleError(
                `There was an error while ${
                    areAllInstallationsVerified ? 'confirming' : 'checking'
                } the serial numbers`,
                err,
                { className: 'mb-20 md:mb-0' }
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setViewIndex(1);
        setInstallations({});
    };

    return (
        <>
            <PublicLayoutContent
                containerProps={{
                    className: '!h-[calc(100%-130px)] md:!h-[calc(100%-80px)]',
                }}
            >
                <ActivationOverviewContext.Provider
                    value={{
                        handleSerialNumberRemove,
                        handleSerialNumberUndo,
                        isPEEnabled,
                        setIsPEEnabled,
                        handleEditConfirm,
                    }}
                >
                    <div
                        className={clsx('px-4 sm:px-14 pt-14 sm:pt-32 h-[calc(100%-80px)]', {
                            'pointer-events-none': isPEEnabled,
                        })}
                    >
                        <div className={clsx('pb-4', { 'blur-sm': isLoading })}>
                            <div className="flex flex-col gap-5 md:flex-row md:items-end mb-10">
                                <div>
                                    <Typography variant="h5" fontWeight={700}>
                                        Uploaded serial numbers overview
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        This is the overview of serial numbers you want to activate, press submit and
                                        process to activate your serial numbers for the given installation.
                                    </Typography>
                                </div>
                                <div className="ml-auto">
                                    <Button
                                        size="large"
                                        disableElevation={false}
                                        color="white"
                                        className="rounded"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={handleBack}
                                    >
                                        <Typography fontWeight={500} variant="body1">
                                            BACK
                                        </Typography>
                                    </Button>
                                </div>
                            </div>
                            <div>
                                {installations &&
                                    Object.keys(installations).map((installationName) => (
                                        <UploadedInstallationOverview
                                            key={installationName}
                                            name={installationName}
                                            data={installations[installationName]}
                                        />
                                    ))}
                            </div>
                        </div>
                        <div className="fixed h-[80px] w-full bg-white bottom-0 left-0 flex items-center justify-end pr-4 md:pr-14 z-[3]">
                            <Button
                                size="large"
                                color="victron-blue"
                                disabled={hasUneditErrors || areInstallationsEmpty}
                                className="!rounded"
                                disableElevation={false}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress size={20} color="white" />
                                    ) : !areAllInstallationsVerified || areInstallationsEmpty ? (
                                        <UploadFileIcon />
                                    ) : (
                                        <CheckBoxIcon />
                                    )
                                }
                                onClick={handleInstallationSubmit}
                            >
                                <Typography variant="body1" fontWeight={500}>
                                    {!areAllInstallationsVerified ? 'RECHECK' : 'CONFIRM'}
                                </Typography>
                            </Button>
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="absolute top-0 left-0 w-full h-full z-10 flex justify-center items-center">
                            <CircularProgress />
                        </div>
                    ) : null}
                </ActivationOverviewContext.Provider>
            </PublicLayoutContent>
        </>
    );
};

export { ActivationOverview };
